(() => {
  const STORAGE_KEY = 'avontic-business-quote-draft-v2';
  const REFERENCE_KEY = 'avontic-business-quote-reference';

  const emit = (name, detail = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...detail });
    document.dispatchEvent(new CustomEvent(`avontic:${name}`, { detail }));
  };

  const referenceNumber = () => {
    const date = new Date().toISOString().slice(0, 10).replaceAll('-', '');
    const random = Math.random().toString(36).slice(2, 10).toUpperCase();
    return `AVB-${date}-${random}`;
  };

  const init = (root) => {
    if (!root || root.dataset.avbReady === 'true') return;
    root.dataset.avbReady = 'true';
    root.querySelectorAll('[data-avb-tools]').forEach((tool) => { tool.hidden = false; });

    const capturedAt = new Date().toISOString();
    const entryContext = [
      `Referrer: ${document.referrer || 'Direct'}`,
      `Landing: ${window.location.pathname}${window.location.search}${window.location.hash}`,
      `Captured: ${capturedAt}`,
    ].join(' | ');
    const entryContexts = [...root.querySelectorAll('[data-avb-entry-context]')];
    const quickForm = root.querySelector('form.avb-quick__form');
    const syncEntryContext = () => {
      entryContexts.forEach((field) => { field.value = entryContext; });
    };
    syncEntryContext();
    root.querySelectorAll('form').forEach((quoteForm) => {
      quoteForm.addEventListener('submit', syncEntryContext, { capture: true });
    });
    quickForm?.addEventListener('submit', () => emit('avb_quick_submit', { source: 'business_quote' }));

    root.querySelectorAll('[data-avb-path]').forEach((pathLink) => {
      pathLink.addEventListener('click', () => emit('avb_path_click', { path: pathLink.dataset.avbPath }));
    });

    const form = root.querySelector('form[data-avb-form], form.avb-quote');
    const successBanners = [...root.querySelectorAll('[data-avb-success-scope]')];
    const referenceTarget = root.querySelector('[data-avb-reference-display]');

    if (successBanners.length) {
      const hash = window.location.hash.replace(/^#/, '');
      if (hash.startsWith('AvonticBusinessQuickQuote')) {
        successBanners.filter((banner) => banner.dataset.avbSuccessScope === 'full').forEach((banner) => { banner.hidden = true; });
      } else if (hash.startsWith('AvonticBusinessQuote')) {
        successBanners.filter((banner) => banner.dataset.avbSuccessScope === 'quick').forEach((banner) => { banner.hidden = true; });
      }
      const visibleBanner = successBanners.find((banner) => !banner.hidden);
      const reference = sessionStorage.getItem(REFERENCE_KEY);
      if (reference && referenceTarget) referenceTarget.textContent = reference;
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem('avontic-business-quote-previous-v3');
      if (visibleBanner) {
        visibleBanner.scrollIntoView({ behavior: 'smooth', block: 'center' });
        visibleBanner.querySelector('h3')?.focus({ preventScroll: true });
      }
      return;
    }

    if (!form) return;

    const panels = [...form.querySelectorAll('[data-avb-step]')];
    const progressFill = form.querySelector('[data-avb-progress-fill]');
    const progressTrack = form.querySelector('[role="progressbar"]');
    const progressCurrent = form.querySelector('[data-avb-progress-current]');
    const progressName = form.querySelector('[data-avb-progress-name]');
    const progressItems = [...form.querySelectorAll('[data-avb-progress-item]')];
    const deviceLines = form.querySelector('[data-avb-device-lines]');
    const deviceTemplate = form.querySelector('[data-avb-device-template]');
    const savedTarget = form.querySelector('[data-avb-saved]');
    const deploymentField = form.querySelector('[data-avb-deployment-field]');
    const procurementField = form.querySelector('[data-avb-procurement-field]');
    const quoteReferenceField = form.querySelector('[data-avb-reference-field]');
    const reviewContact = form.querySelector('[data-avb-review-contact]');
    const reviewDeployment = form.querySelector('[data-avb-review-deployment]');
    const reviewProcurement = form.querySelector('[data-avb-review-procurement]');
    const workspaceStatus = form.querySelector('[data-avb-workspace-status]');
    const csvInput = form.querySelector('[data-avb-csv-file]');
    const csvStatus = form.querySelector('[data-avb-csv-status]');
    const quantityNotice = form.querySelector('[data-avb-quantity-notice]');
    const readinessContact = form.querySelector('[data-avb-ready-contact]');
    const readinessDevices = form.querySelector('[data-avb-ready-devices]');
    const readinessTimeline = form.querySelector('[data-avb-ready-timeline]');
    const readinessProcurement = form.querySelector('[data-avb-ready-procurement]');
    const errorRequired = root.dataset.errorRequired || 'Please complete this required field.';
    const errorEmail = root.dataset.errorEmail || 'Enter a valid work email.';
    const errorANumber = root.dataset.errorANumber || 'Use A followed by four digits, or leave this blank.';
    const stepNames = (root.dataset.stepNames || 'Organization|Deployment|Procurement|Review').split('|');
    let step = 0;
    let deviceIndex = 1;
    let saveTimer;
    let started = false;
    let errorIndex = 0;

    const fieldValue = (selector) => form.querySelector(selector)?.value?.trim() || '';
    const checkedValue = (name) => form.querySelector(`[name="${name}"]:checked`)?.value || '';
    const selectedProductLabel = () => {
      const selected = form.querySelector('[name="contact[Case preference]"]:checked');
      return selected?.closest('label')?.querySelector('strong')?.textContent?.trim() || selected?.value || '';
    };

    const setError = (field, message = '') => {
      if (!field) return;
      field.setAttribute('aria-invalid', message ? 'true' : 'false');
      const error = field.closest('.avb-field')?.querySelector('.avb-field-error');
      if (error) {
        if (!error.id) {
          errorIndex += 1;
          error.id = `avb-field-error-${root.id}-${errorIndex}`;
        }
        error.textContent = message;
        if (message) field.setAttribute('aria-describedby', error.id);
        else if (field.getAttribute('aria-describedby') === error.id) field.removeAttribute('aria-describedby');
      }
    };

    const validateField = (field) => {
      if (!field || field.disabled || field.type === 'hidden') return true;
      let message = '';
      const value = field.value?.trim() || '';

      if (field.required && field.type === 'checkbox' && !field.checked) message = errorRequired;
      else if (field.required && !value) message = errorRequired;
      else if (field.type === 'email' && value && !/^\S+@\S+\.\S+$/.test(value)) message = errorEmail;
      else if (field.matches('[data-avb-a-number]') && value && !/^A\d{4}$/i.test(value)) message = errorANumber;
      else if (field.type === 'number' && value && Number(value) < Number(field.min || 0)) message = errorRequired;

      setError(field, message);
      return !message;
    };

    const validateStep = (targetStep) => {
      const panel = panels[targetStep];
      if (!panel) return true;
      const fields = [...panel.querySelectorAll('input, select, textarea')];
      let valid = true;
      fields.forEach((field) => {
        if (!validateField(field)) valid = false;
      });

      if (targetStep === 1) {
        const productChoice = form.querySelector('[name="contact[Case preference]"]:checked');
        if (!productChoice) valid = false;
      }

      if (!valid) panel.querySelector('[aria-invalid="true"], input:required, select:required, textarea:required')?.focus();
      return valid;
    };

    const updateProgress = ({ focus = false, scroll = false } = {}) => {
      panels.forEach((panel, index) => {
        const inactive = index !== step;
        panel.hidden = inactive;
        panel.setAttribute('aria-hidden', inactive ? 'true' : 'false');
        panel.toggleAttribute('inert', inactive);
      });
      if (progressFill) progressFill.style.width = `${((step + 1) / panels.length) * 100}%`;
      if (progressTrack) {
        progressTrack.setAttribute('aria-valuenow', String(step + 1));
        progressTrack.setAttribute('aria-valuetext', stepNames[step] || '');
      }
      if (progressCurrent) progressCurrent.textContent = String(step + 1);
      if (progressName) progressName.textContent = stepNames[step] || '';
      progressItems.forEach((item, index) => {
        item.classList.toggle('is-active', index === step);
        item.classList.toggle('is-complete', index < step);
        if (index === step) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
      if (focus) panels[step]?.querySelector('h3')?.focus({ preventScroll: true });
      if (scroll) {
        form.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      }
    };

    const addDeviceLine = (values = null) => {
      if (!deviceLines || !deviceTemplate || deviceLines.children.length >= 24) return null;
      const html = deviceTemplate.innerHTML.replaceAll('__INDEX__', String(deviceIndex));
      const holder = document.createElement('div');
      holder.innerHTML = html.trim();
      const line = holder.firstElementChild;
      if (!line) return null;
      deviceLines.append(line);
      if (values) {
        Object.entries(values).forEach(([key, value]) => {
          const field = line.querySelector(`[data-device-key="${key}"]`);
          if (field) field.value = String(value ?? '');
        });
      }
      deviceIndex += 1;
      updateLineLabels();
      return line;
    };

    const updateLineLabels = () => {
      [...(deviceLines?.querySelectorAll('[data-avb-device-line]') || [])].forEach((line, index) => {
        const label = line.querySelector('[data-device-line-label]');
        if (label) {
          label.replaceChildren(document.createTextNode(`${root.dataset.deviceLineLabel || 'Device line'} `));
          const lineNumber = document.createElement('span');
          lineNumber.className = 'sr-only';
          lineNumber.textContent = String(index + 1);
          label.append(lineNumber);
        }
        const remove = line.querySelector('[data-avb-remove-line]');
        if (remove) remove.hidden = deviceLines.children.length === 1;
      });
    };

    const serializeDevices = () => [...(deviceLines?.querySelectorAll('[data-avb-device-line]') || [])].map((line, index) => ({
      line: index + 1,
      model: line.querySelector('[data-device-key="model"]')?.value?.trim() || '',
      aNumber: line.querySelector('[data-device-key="aNumber"]')?.value?.trim().toUpperCase() || '',
      quantity: line.querySelector('[data-device-key="quantity"]')?.value?.trim() || '',
      colors: line.querySelector('[data-device-key="colors"]')?.value?.trim() || '',
    }));

    const updateQuantityNotice = () => {
      if (!quantityNotice) return;
      const estimated = Number(fieldValue('[data-avb-total-quantity]')) || 0;
      const lineTotal = serializeDevices().reduce((total, item) => total + (Number(item.quantity) || 0), 0);
      quantityNotice.classList.remove('is-match', 'is-mismatch');
      if (!estimated && !lineTotal) {
        quantityNotice.textContent = '';
        return;
      }
      if (estimated === lineTotal) {
        quantityNotice.classList.add('is-match');
        quantityNotice.textContent = `${root.dataset.quantityMatch || 'Device-line total matches the estimated quantity:'} ${lineTotal}.`;
      } else {
        quantityNotice.classList.add('is-mismatch');
        quantityNotice.textContent = `${root.dataset.quantityMismatch || 'Review the quantity totals:'} ${lineTotal} / ${estimated}.`;
      }
    };

    const selectedNeeds = () => [...form.querySelectorAll('[data-avb-procurement-need]:checked')].map((field) => field.value);

    const buildDeploymentText = () => {
      const lines = serializeDevices().map((item) => {
        const aNumber = item.aNumber ? ` | A-number: ${item.aNumber}` : '';
        return `Line ${item.line}: ${item.model}${aNumber} | Qty: ${item.quantity} | Color: ${item.colors || 'No preference'}`;
      });
      return [`Product preference: ${selectedProductLabel()}`, `Total estimated quantity: ${fieldValue('[data-avb-total-quantity]')}`, ...lines, fieldValue('[data-avb-selected-setups]')].filter(Boolean).join('\n');
    };

    const buildProcurementText = () => {
      const needs = selectedNeeds();
      return [
        `Desired timeframe: ${fieldValue('[data-avb-timeframe]')}`,
        `Shipping postal code: ${fieldValue('[data-avb-postal]')}`,
        `Purchasing needs: ${needs.length ? needs.join(', ') : 'Standard quote review'}`,
        `Accessories: ${fieldValue('[data-avb-accessories]') || 'None specified'}`,
        `Notes: ${fieldValue('[data-avb-notes]') || 'None specified'}`,
      ].join('\n');
    };

    const ensureReference = () => {
      let reference = quoteReferenceField?.value || sessionStorage.getItem(REFERENCE_KEY) || '';
      if (!reference) reference = referenceNumber();
      if (quoteReferenceField) quoteReferenceField.value = reference;
      sessionStorage.setItem(REFERENCE_KEY, reference);
      return reference;
    };

    const setReady = (target, ready) => {
      if (target) target.classList.toggle('is-ready', Boolean(ready));
    };

    const updateReview = () => {
      if (reviewContact) {
        reviewContact.textContent = [
          `${fieldValue('[name="contact[First name]"]')} ${fieldValue('[name="contact[Last name]"]')}`.trim(),
          fieldValue('[name="contact[Organization]"]'),
          fieldValue('[name="contact[email]"]'),
          fieldValue('[name="contact[phone]"]'),
        ].filter(Boolean).join('\n');
      }
      if (reviewDeployment) reviewDeployment.textContent = buildDeploymentText();
      if (reviewProcurement) reviewProcurement.textContent = buildProcurementText();
      if (deploymentField) deploymentField.value = buildDeploymentText();
      if (procurementField) procurementField.value = buildProcurementText();
      const devices = serializeDevices();
      setReady(readinessContact, Boolean(fieldValue('[name="contact[First name]"]') && fieldValue('[name="contact[Organization]"]') && fieldValue('[name="contact[email]"]')));
      setReady(readinessDevices, devices.length > 0 && devices.every((item) => item.model && Number(item.quantity) > 0));
      setReady(readinessTimeline, Boolean(fieldValue('[data-avb-timeframe]') && fieldValue('[data-avb-postal]')));
      setReady(readinessProcurement, selectedNeeds().length > 0 || Boolean(fieldValue('[data-avb-notes]') || fieldValue('[data-avb-accessories]')));
      updateQuantityNotice();
    };

    const buildSummaryText = () => {
      updateReview();
      const contactLabel = reviewContact?.previousElementSibling?.textContent?.trim() || 'Contact';
      const deploymentLabel = reviewDeployment?.previousElementSibling?.textContent?.trim() || 'Deployment';
      const procurementLabel = reviewProcurement?.previousElementSibling?.textContent?.trim() || 'Procurement';
      return [
        root.dataset.summaryTitle || 'Avontic deployment planning summary',
        `${root.dataset.summaryReference || 'Request reference:'} ${ensureReference()}`,
        `${root.dataset.summaryPrepared || 'Prepared'}: ${new Date().toLocaleString()}`,
        '',
        contactLabel.toUpperCase(),
        reviewContact?.textContent?.trim() || '',
        '',
        deploymentLabel.toUpperCase(),
        reviewDeployment?.textContent?.trim() || '',
        '',
        procurementLabel.toUpperCase(),
        reviewProcurement?.textContent?.trim() || '',
        '',
        root.dataset.summaryDisclaimer || 'Planning summary only. Pricing, availability, compatibility, and purchasing terms remain subject to Avontic review.',
      ].join('\n');
    };

    const draftObject = () => ({
      fields: [...form.querySelectorAll('input, select, textarea')]
        .filter((field) => field.name && !field.matches('[data-avb-deployment-field],[data-avb-procurement-field],[data-avb-reference-field]'))
        .map((field) => ({ name: field.name, type: field.type, value: field.value, checked: field.checked })),
      devices: serializeDevices(),
      step,
      savedAt: Date.now(),
    });

    const persistDraft = (explicit = false) => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(draftObject()));
        const timestamp = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
        if (savedTarget) savedTarget.textContent = `${root.dataset.savedLabel || 'Draft saved'} ${timestamp}`;
        if (explicit && workspaceStatus) workspaceStatus.textContent = `${root.dataset.savedNowLabel || 'Saved in this browser at'} ${timestamp}.`;
        return true;
      } catch (_) {
        if (savedTarget) savedTarget.textContent = '';
        return false;
      }
    };

    const saveDraft = () => {
      window.clearTimeout(saveTimer);
      saveTimer = window.setTimeout(() => persistDraft(false), 300);
    };

    const restoreDraft = (provided) => {
      let draft = provided;
      if (!draft) { try { draft = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch (_) { draft = null; } }
      if (!draft?.fields) return false;

      if (Array.isArray(draft.devices) && draft.devices.length) {
        while (deviceLines.children.length > 1) deviceLines.lastElementChild.remove();
        const first = deviceLines.querySelector('[data-avb-device-line]');
        Object.entries(draft.devices[0]).forEach(([key, value]) => {
          const field = first?.querySelector(`[data-device-key="${key}"]`);
          if (field) field.value = String(value ?? '');
        });
        draft.devices.slice(1).forEach(addDeviceLine);
      }

      draft.fields.forEach((saved) => {
        if (saved.name.startsWith('avb_device_')) return;
        const candidates = [...form.querySelectorAll(`[name="${CSS.escape(saved.name)}"]`)];
        const field = candidates.find((candidate) => candidate.type === 'radio' ? candidate.value === saved.value : true);
        if (!field) return;
        if (field.type === 'checkbox' || field.type === 'radio') field.checked = Boolean(saved.checked);
        else field.value = saved.value ?? '';
      });
      step = Math.max(0, Math.min(Number(draft.step) || 0, panels.length - 1));
      updateLineLabels();
      return true;
    };

    const restored = restoreDraft();
    if (restored && workspaceStatus) workspaceStatus.textContent = root.dataset.restoredLabel || 'Your saved draft was restored in this browser.';
    if (!restored) {
      const product = new URLSearchParams(window.location.search).get('product');
      const preselected = form.querySelector(`[name="contact[Case preference]"][value="${CSS.escape(product || '')}"]`);
      if (preselected) preselected.checked = true;
    }

    const downloadBlob = (content, filename, type = 'text/plain;charset=utf-8') => {
      const url = URL.createObjectURL(new Blob([content], { type }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    const delimiterCount = (line, delimiter) => {
      let count = 0;
      let quoted = false;
      for (let index = 0; index < line.length; index += 1) {
        if (line[index] === '"') quoted = !quoted;
        else if (!quoted && line[index] === delimiter) count += 1;
      }
      return count;
    };

    const parseCsv = (source) => {
      const text = source.replace(/^\uFEFF/, '').trim();
      if (!text) return [];
      const firstLine = text.split(/\r?\n/).find((line) => line.trim()) || '';
      const delimiters = [',', ';', '\t'];
      const delimiter = delimiters.sort((a, b) => delimiterCount(firstLine, b) - delimiterCount(firstLine, a))[0];
      const rows = [];
      let row = [];
      let cell = '';
      let quoted = false;
      for (let index = 0; index < text.length; index += 1) {
        const character = text[index];
        if (character === '"') {
          if (quoted && text[index + 1] === '"') {
            cell += '"';
            index += 1;
          } else {
            quoted = !quoted;
          }
        } else if (character === delimiter && !quoted) {
          row.push(cell.trim());
          cell = '';
        } else if ((character === '\n' || character === '\r') && !quoted) {
          if (character === '\r' && text[index + 1] === '\n') index += 1;
          row.push(cell.trim());
          if (row.some(Boolean)) rows.push(row);
          row = [];
          cell = '';
        } else {
          cell += character;
        }
      }
      row.push(cell.trim());
      if (row.some(Boolean)) rows.push(row);
      return rows;
    };

    const normalizeHeader = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

    const csvDeviceRows = (rows) => {
      if (rows.length < 2) return [];
      const headers = rows[0].map(normalizeHeader);
      const aliases = {
        model: ['model', 'device', 'device model', 'ipad model', 'tablet model'],
        aNumber: ['a number', 'anumber', 'apple a number'],
        quantity: ['quantity', 'qty', 'units', 'unit count'],
        colors: ['color', 'colors', 'preferred color', 'preferred colors'],
      };
      const indexFor = (key) => headers.findIndex((header) => aliases[key].includes(header));
      const indexes = {
        model: indexFor('model'),
        aNumber: indexFor('aNumber'),
        quantity: indexFor('quantity'),
        colors: indexFor('colors'),
      };
      if (indexes.model < 0 || indexes.quantity < 0) return null;
      return rows.slice(1).map((row) => ({
        model: row[indexes.model]?.trim() || '',
        aNumber: indexes.aNumber >= 0 ? (row[indexes.aNumber]?.trim().toUpperCase() || '') : '',
        quantity: row[indexes.quantity]?.trim() || '',
        colors: indexes.colors >= 0 ? (row[indexes.colors]?.trim() || '') : '',
      })).filter((item) => item.model || item.quantity);
    };

    const replaceDeviceLines = (devices) => {
      while (deviceLines.children.length > 1) deviceLines.lastElementChild.remove();
      const first = deviceLines.querySelector('[data-avb-device-line]');
      const firstValues = devices[0];
      ['model', 'aNumber', 'quantity', 'colors'].forEach((key) => {
        const field = first?.querySelector(`[data-device-key="${key}"]`);
        if (field) field.value = String(firstValues?.[key] ?? '');
      });
      devices.slice(1).forEach(addDeviceLine);
      updateLineLabels();
      updateQuantityNotice();
      updateReview();
    };

    const syncQuoteForms = () => {
      const total = fieldValue('[data-avb-total-quantity]');
      const quickTotal = quickForm?.querySelector('[name="contact[Estimated quantity]"]');
      if (quickTotal) quickTotal.value = total;
      const quickSetups = quickForm?.querySelector('[data-avb-selected-setups]');
      if (quickSetups) quickSetups.value = fieldValue('[data-avb-selected-setups]');
      const quickDeployment = quickForm?.querySelector('[data-avb-quick-deployment]');
      if (quickDeployment) quickDeployment.value = buildDeploymentText();
      root.dispatchEvent(new CustomEvent('avontic:quote-updated'));
    };
    root.addEventListener('avontic:quote-selection', event => {
      const request = event.detail;
      if (!request) return;
      if (request.mode === 'resume') {
        if (!restoreDraft(request.draft)) return;
      } else {
        const item = request.selection;
        if (!item || !Number.isInteger(item.quantity) || item.quantity < 1) return;
        if (request.mode === 'append' && request.draft?.devices?.length >= 24) return;
        if (request.mode === 'append' && request.draft) restoreDraft(request.draft);
        const previous = request.mode === 'append' ? serializeDevices().filter(line => line.model || Number(line.quantity) > 0) : [];
        if (previous.length + (item.devices?.length || 1) > 24) return;
        const previousTotal = request.mode === 'append' ? Math.max(Number(fieldValue('[data-avb-total-quantity]')) || 0, previous.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0)) : 0;
        const next = { model: item.model || item.title, aNumber: item.aNumber || '', quantity: item.quantity, colors: item.colors || '' };
        replaceDeviceLines([...previous, ...(item.devices || [next])]);
        const totalField = form.querySelector('[data-avb-total-quantity]');
        if (totalField) totalField.value = String(previousTotal + item.quantity);
        const setupsField = form.querySelector('[data-avb-selected-setups]');
        if (setupsField) setupsField.value = [request.mode === 'append' ? setupsField.value : '', item.summary].filter(Boolean).join('\n\n');
        const keyField = form.querySelector('[data-avb-selection-key]');
        if (keyField) keyField.value = item.key;
        const preferred = request.mode === 'append' ? 'help' : item.preference;
        const radio = [...form.querySelectorAll('[name="contact[Case preference]"]')].find(field => field.value === preferred);
        if (radio) radio.checked = true;
        step = 0;
      }
      updateReview();
      updateProgress();
      syncQuoteForms();
      persistDraft(false);
      request.applied = true;
    });
    form.addEventListener('input', syncQuoteForms);
    form.addEventListener('change', syncQuoteForms);
    quickForm?.querySelector('[name="contact[Estimated quantity]"]')?.addEventListener('input', event => {
      const total = form.querySelector('[data-avb-total-quantity]');
      if (total) total.value = event.target.value;
      updateReview();
      syncQuoteForms();
      saveDraft();
    });
    syncQuoteForms();

    form.querySelector('[data-avb-import-csv]')?.addEventListener('click', async () => {
      const file = csvInput?.files?.[0];
      if (!file) {
        if (csvStatus) csvStatus.textContent = root.dataset.csvEmpty || 'Choose a CSV file first.';
        csvInput?.focus();
        return;
      }
      if (file.size > 262144) {
        if (csvStatus) csvStatus.textContent = root.dataset.csvSize || 'Use a CSV file smaller than 256 KB.';
        return;
      }
      let rows;
      try {
        rows = csvDeviceRows(parseCsv(await file.text()));
      } catch (_) {
        rows = [];
      }
      if (rows === null) {
        if (csvStatus) csvStatus.textContent = root.dataset.csvColumns || 'Include Model and Quantity columns.';
        return;
      }
      if (!rows?.length) {
        if (csvStatus) csvStatus.textContent = root.dataset.csvEmpty || 'No device rows were found.';
        return;
      }
      const accepted = rows.slice(0, 24);
      replaceDeviceLines(accepted);
      const importedTotal = accepted.reduce((total, item) => total + (Number(item.quantity) || 0), 0);
      const totalField = form.querySelector('[data-avb-total-quantity]');
      if (totalField && importedTotal > 0) totalField.value = String(importedTotal);
      updateQuantityNotice();
      syncQuoteForms();
      persistDraft(false);
      const limitNote = rows.length > accepted.length ? ` ${root.dataset.csvLimit || 'Only the first 24 rows were imported.'}` : '';
      if (csvStatus) csvStatus.textContent = `${root.dataset.csvImported || 'Device rows imported:'} ${accepted.length}.${limitNote}`;
      emit('b2b_device_csv_imported', { row_count: accepted.length, total_quantity: importedTotal });
    });

    form.querySelector('[data-avb-download-template]')?.addEventListener('click', () => {
      downloadBlob('Model,A-number,Quantity,Colors\n"iPad 10th generation",A2696,25,Black\n', 'avontic-device-list-template.csv', 'text/csv;charset=utf-8');
    });

    form.querySelector('[data-avb-save-draft]')?.addEventListener('click', () => {
      persistDraft(true);
      emit('b2b_quote_draft_saved');
    });

    form.querySelector('[data-avb-restore-draft]')?.addEventListener('click', () => {
      if (!restoreDraft()) {
        if (workspaceStatus) workspaceStatus.textContent = root.dataset.noDraftLabel || 'No saved draft is available in this browser.';
        return;
      }
      updateReview();
      syncQuoteForms();
      updateProgress({ focus: true, scroll: true });
      if (workspaceStatus) workspaceStatus.textContent = root.dataset.restoredLabel || 'Your saved draft was restored in this browser.';
      emit('b2b_quote_draft_restored');
    });

    form.querySelector('[data-avb-clear-draft]')?.addEventListener('click', () => {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem('avontic-business-quote-previous-v3');
      root.dispatchEvent(new CustomEvent('avontic:quote-cleared'));
      if (savedTarget) savedTarget.textContent = '';
      if (workspaceStatus) workspaceStatus.textContent = root.dataset.clearedLabel || 'The saved browser draft was cleared. Current entries remain on screen.';
      emit('b2b_quote_draft_cleared');
    });

    form.querySelector('[data-avb-print-summary]')?.addEventListener('click', () => {
      updateReview();
      ensureReference();
      document.body.classList.add('avb-printing-summary');
      window.print();
      window.setTimeout(() => document.body.classList.remove('avb-printing-summary'), 250);
      emit('b2b_quote_summary_printed');
    });

    form.querySelector('[data-avb-download-summary]')?.addEventListener('click', () => {
      const reference = ensureReference();
      downloadBlob(buildSummaryText(), `Avontic-deployment-${reference}.txt`);
      emit('b2b_quote_summary_downloaded', { quote_reference: reference });
    });

    form.addEventListener('input', (event) => {
      started = true;
      if (event.target.matches('[data-avb-a-number]')) event.target.value = event.target.value.toUpperCase();
      setError(event.target, '');
      updateQuantityNotice();
      saveDraft();
    });
    form.addEventListener('change', () => {
      started = true;
      updateQuantityNotice();
      saveDraft();
    });

    form.addEventListener('click', (event) => {
      const next = event.target.closest('[data-avb-next]');
      const back = event.target.closest('[data-avb-back]');
      const add = event.target.closest('[data-avb-add-line]');
      const remove = event.target.closest('[data-avb-remove-line]');
      const goStep = event.target.closest('[data-avb-go-step]');

      if (next) {
        if (!validateStep(step)) return;
        emit('avb_quote_step', { step_index: step + 1, step_name: stepNames[step] });
        emit('b2b_quote_step_completed', { step: step + 1, step_name: stepNames[step] });
        step = Math.min(step + 1, panels.length - 1);
        if (step === panels.length - 1) {
          ensureReference();
          updateReview();
        }
        updateProgress({ focus: true, scroll: true });
        saveDraft();
      }
      if (back) {
        step = Math.max(step - 1, 0);
        emit('avb_quote_back', { step_index: step + 1, step_name: stepNames[step] });
        updateProgress({ focus: true, scroll: true });
        saveDraft();
      }
      if (add) {
        const addedLine = addDeviceLine();
        addedLine?.querySelector('input, select, textarea')?.focus();
        emit('b2b_device_line_added', { line_count: deviceLines.children.length });
        saveDraft();
      }
      if (remove) {
        const removedLine = remove.closest('[data-avb-device-line]');
        const focusTargetLine = removedLine?.nextElementSibling || removedLine?.previousElementSibling;
        removedLine?.remove();
        updateLineLabels();
        updateQuantityNotice();
        (focusTargetLine?.querySelector('input, select, textarea') || add)?.focus();
        saveDraft();
      }
      if (goStep) {
        step = Math.max(0, Math.min(Number(goStep.dataset.avbGoStep) || 0, panels.length - 1));
        updateProgress({ focus: true, scroll: true });
        saveDraft();
      }
    });

    form.addEventListener('submit', (event) => {
      for (let index = 0; index < panels.length; index += 1) {
        if (!validateStep(index)) {
          event.preventDefault();
          step = index;
          updateProgress({ focus: true, scroll: true });
          return;
        }
      }
      updateReview();
      const reference = ensureReference();
      persistDraft(false);
      emit('avb_quote_submit', {
        product_choice: checkedValue('contact[Case preference]'),
        estimated_quantity: Number(fieldValue('[data-avb-total-quantity]')) || 0,
        device_line_count: serializeDevices().length,
        quote_reference: reference,
      });
      emit('b2b_quote_submitted', {
        product_choice: checkedValue('contact[Case preference]'),
        estimated_quantity: Number(fieldValue('[data-avb-total-quantity]')) || 0,
        device_line_count: serializeDevices().length,
        quote_reference: reference,
      });
    });

    form.addEventListener('focusin', () => {
      if (!started) {
        started = true;
        emit('b2b_quote_started');
      }
    }, { once: true });

    updateLineLabels();
    updateReview();
    updateProgress();
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-avb]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();

